/**
 * Le même virement, lu une fois dans chaque fichier — et les deux gestes qui
 * tranchent : réunir les deux lignes, ou dire qu'il s'agit de deux virements.
 *
 * Né du 27/09/2026 : 48 mouvements écrits deux fois par un import de relevés
 * CODA sur une période que l'export CSV couvrait déjà. L'import ne le refait
 * plus, mais un doublon PROBABLE (même date, même montant, même compte, autre
 * communication) est encore écrit, par prudence : deux virements distincts
 * peuvent se ressembler à ce point. C'est ici qu'un humain tranche, pour les
 * entrées (file de rapprochement) comme pour les sorties (dépenses à placer).
 *
 * « Réunir » garde l'AUTRE ligne et retire celle-ci : c'est elle qui attend,
 * l'autre porte souvent déjà le placement. La base refuse de retirer une
 * ligne placée vers une famille, et archive ce qu'elle retire.
 * « Deux virements distincts » consigne le jugement (qui, quand, pourquoi) :
 * le contrôle et la file ne les signalent plus.
 */

import { useState } from 'react';
import { Copy, Split } from 'lucide-react';
import { declarerDistincts, reunirLesDeuxLignes } from '../../../lib/payments/jumeaux.supabase';
import type { JumeauEnFile } from '../../../lib/payments/queueReason';
import { dateCourte, euros } from './money';

const SOURCES_EN_CLAIR: Record<string, string> = {
  bank: 'relevé CODA',
  bank_csv: 'export CSV',
};

export default function VirementLuDeuxFois({
  sens, ligne, jumeau, enCours = false, executer,
}: {
  sens: 'entree' | 'sortie';
  ligne: { id: string; amountCents: number; unallocatedCents: number };
  jumeau: JumeauEnFile;
  enCours?: boolean;
  executer: (action: () => Promise<void>, message: string) => Promise<void>;
}) {
  const [verifie, setVerifie] = useState(false);
  const [motifDistincts, setMotifDistincts] = useState('');
  const j = jumeau;

  const dejaPlace = ligne.unallocatedCents !== ligne.amountCents;
  const douteux = j.communications === 'differentes';
  /* La ligne en face vient de l'autre source : celle-ci vient donc de la sienne. */
  const sourceIci = j.source === 'bank' ? 'bank_csv' : 'bank';
  const ici = SOURCES_EN_CLAIR[sourceIci];
  const labas = SOURCES_EN_CLAIR[j.source] ?? j.source;
  const idReleve = j.source === 'bank' ? j.id : ligne.id;
  const idExport = j.source === 'bank' ? ligne.id : j.id;

  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
      <p className="flex items-center gap-1.5 font-medium">
        <Copy className="h-4 w-4" /> Probablement le même virement, lu deux fois
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
        <li>celui-ci vient du <strong>{ici}</strong> ;</li>
        <li>
          l'autre, du <strong>{labas}</strong>, même date valeur ({dateCourte(j.valueDate)}), même montant,
          même compte{j.placeCents > 0
            ? <>, déjà placé : {euros(j.placeCents)}{j.placeSur.length > 0 && <> sur {j.placeSur.join(', ')}</>}</>
            : <>, en attente lui aussi</>} ;
        </li>
        <li>
          {j.communications === 'identiques' && 'les deux portent la même communication.'}
          {j.communications === 'une_absente' && "l'une des deux ne porte aucune communication."}
          {douteux && "les communications diffèrent : vérifiez sur l'extrait de compte qu'il n'y a qu'un virement."}
        </li>
      </ul>

      {dejaPlace ? (
        <p className="mt-2 text-xs">
          Ce virement porte déjà un placement : défaites-le d'abord, puis réunissez les deux lignes.
        </p>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {douteux && (
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={verifie} onChange={(e) => setVerifie(e.target.checked)} />
              J'ai vérifié sur l'extrait : c'est le même virement.
            </label>
          )}
          <button
            disabled={enCours || (douteux && !verifie)}
            onClick={() => executer(
              () => reunirLesDeuxLignes(sens, j.id, ligne.id,
                `Réuni depuis la file : le même virement lu dans le ${ici} et dans le ${labas} (communications ${j.communications.replace('_', ' ')})`),
              'Les deux lignes sont réunies : ce virement ne compte plus qu’une fois',
            )}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-50"
          >
            <Copy className="h-3.5 w-3.5" /> Réunir : garder la ligne du {labas}, retirer celle-ci
          </button>
          <span className="text-xs text-red-800">La ligne retirée est archivée, rien n'est perdu.</span>
        </div>
      )}

      {/*
        La répétition légitime : un parent qui règle deux factures du même
        montant le même jour. Le dire, avec la raison, fait taire le signal —
        sans rien retirer.
      */}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-red-200 pt-2">
        <input
          value={motifDistincts}
          onChange={(e) => setMotifDistincts(e.target.value)}
          placeholder="Pourquoi ce sont deux virements (vérifié sur l'extrait…)"
          className="min-w-0 flex-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs"
        />
        <button
          disabled={enCours || motifDistincts.trim() === ''}
          onClick={() => executer(
            () => declarerDistincts(sens, idReleve, idExport, motifDistincts.trim()),
            'Noté : ce sont deux virements distincts',
          )}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 disabled:opacity-50"
        >
          <Split className="h-3.5 w-3.5" /> Ce sont deux virements distincts
        </button>
      </div>
    </div>
  );
}
